import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const fmt = n => Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => {
  if (!d) return '-';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/^(\d+) (\w+) (\d+)$/, '$1 $2, $3');
};

export default function Consumers() {
  const [consumers, setConsumers] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [filters, setFilters] = useState({ barangay_id: '', search: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 25;
  const [showAll, setShowAll] = useState(false);

  // Ledger modal state
  const [ledgerModal, setLedgerModal] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Balance adjustment state
  const [adjForm, setAdjForm] = useState({ billing_period: '', water_charge: '', past_due: '', penalty: '', payment_amount: '' });
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjError, setAdjError] = useState('');

  // Add modal state
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ account_code: '', name: '', barangay_id: '', address: '', mobile: '', consumer_type: 'Residential', status: 'Active' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit modal state
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ account_code: '', name: '', barangay_id: '', address: '', mobile: '', consumer_type: 'Residential', status: 'Active' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const load = (p, all) => {
    setLoading(true);
    const useAll = all !== undefined ? all : showAll;
    const noPagination = useAll || filters.barangay_id;
    const params = noPagination ? { per_page: 99999 } : { page: p || page, per_page: perPage };
    if (filters.barangay_id) params.barangay_id = filters.barangay_id;
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    api.get('/consumers', { params }).then(r => {
      setConsumers(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.last_page);
      setPage(r.data.page);
      setLoading(false);
    });
  };

  useEffect(() => { api.get('/barangays').then(r => setBarangays(r.data)); }, []);
  useEffect(() => { load(1, showAll); }, [filters.barangay_id, filters.status, showAll]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { load(1, showAll); }, 300);
    return () => clearTimeout(t);
  }, [filters.search]); // eslint-disable-line

  const statusBadge = s => {
    const map = { Active: 'badge-green', Inactive: 'badge-gray', Disconnected: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const billStatusBadge = s => {
    const map = { Paid: 'badge-green', Unpaid: 'badge-orange', Overdue: 'badge-red', Partial: 'badge-blue' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const goPage = (p) => { if (p >= 1 && p <= totalPages) load(p); };

  const openLedger = (consumerId) => {
    setLedgerLoading(true);
    setLedgerModal({ consumer: null, ledger: [] });
    api.get(`/consumers/${consumerId}/ledger`).then(r => {
      setLedgerModal(r.data);
      setLedgerLoading(false);
    });
  };

  const openEdit = (consumerId) => {
    setEditLoading(true);
    setEditError('');
    setEditModal(consumerId);
    api.get(`/consumers/${consumerId}`).then(r => {
      const c = r.data.consumer;
      setEditForm({
        account_code: c.account_code, name: c.name, barangay_id: String(c.barangay_id),
        address: c.address || '', mobile: c.mobile || '', consumer_type: c.consumer_type, status: c.status,
      });
      setEditLoading(false);
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      const data = { ...editForm, barangay_id: parseInt(editForm.barangay_id) };
      await api.put(`/consumers/${editModal}`, data);
      setEditModal(null);
      load(page);
    } catch (err) {
      setEditError(err.response?.data?.message || err.message);
    }
    setEditSaving(false);
  };

  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const openAdd = () => {
    setAddForm({ account_code: '', name: '', barangay_id: '', address: '', mobile: '', consumer_type: 'Residential', status: 'Active' });
    setAddError('');
    setAddModal(true);
  };

  const setAdd = (k, v) => setAddForm(f => ({ ...f, [k]: v }));

  const fetchNextCode = async (barangayId) => {
    if (!barangayId) return;
    const r = await api.get(`/consumers/next_code/${barangayId}`);
    setAddForm(f => ({ ...f, account_code: r.data.next_code || f.account_code }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddSaving(true);
    setAddError('');
    try {
      const data = { ...addForm, barangay_id: parseInt(addForm.barangay_id) };
      await api.post('/consumers', data);
      setAddModal(false);
      load(page);
    } catch (err) {
      setAddError(err.response?.data?.message || err.message);
    }
    setAddSaving(false);
  };

  const openAdj = () => {
    setAdjForm({ billing_period: '', water_charge: '', past_due: '', penalty: '', payment_amount: '' });
    setAdjError('');
    setAdjOpen(true);
  };

  const handleAdjSubmit = async (e) => {
    e.preventDefault();
    if (!ledgerModal?.consumer) return;
    setAdjSaving(true);
    setAdjError('');
    try {
      await api.post('/bills/adjustment', {
        consumer_id: ledgerModal.consumer.id,
        billing_period: adjForm.billing_period,
        water_charge: parseFloat(adjForm.water_charge) || 0,
        past_due: parseFloat(adjForm.past_due) || 0,
        penalty: parseFloat(adjForm.penalty) || 0,
        payment_amount: parseFloat(adjForm.payment_amount) || 0,
      });
      setAdjOpen(false);
      // Refresh ledger
      openLedger(ledgerModal.consumer.id);
    } catch (err) {
      setAdjError(err.response?.data?.message || err.message);
    }
    setAdjSaving(false);
  };

  const printLedger = () => {
    if (!ledgerModal) return;
    const { consumer, ledger } = ledgerModal;
    const w = window.open('', '_blank');
    const rows = ledger.map(l => `
      <tr>
        <td>${fmtDate(l.reading_date)}</td>
        <td style="text-align:right">${l.previous_reading ?? '-'}</td>
        <td style="text-align:right">${l.current_reading}</td>
        <td style="text-align:right">${l.consumption}</td>
        <td style="text-align:right">${fmt(l.bill_amount)}</td>
        <td style="text-align:right">${fmt(l.paid_amount)}</td>
        <td style="text-align:right">${fmt(l.balance)}</td>
        <td>${l.status || '-'}</td>
      </tr>
    `).join('');
    w.document.write(`<!DOCTYPE html><html><head><title>Meter Reading Ledger</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        h2, h3 { margin: 4px 0; }
        .info { margin: 8px 0 16px; color: #555; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 5px 8px; }
        th { background: #f0f0f0; text-align: left; }
        @media print { body { margin: 10px; } }
      </style></head><body>
      <h2>Meter Reading Ledger</h2>
      <h3>${consumer.name}</h3>
      <div class="info">${consumer.account_code} &bull; ${consumer.barangay_name} &bull; ${consumer.consumer_type}</div>
      <table>
        <thead><tr><th>Date</th><th style="text-align:right">Prev</th><th style="text-align:right">Current</th><th style="text-align:right">Cons.</th><th style="text-align:right">Billed</th><th style="text-align:right">Paid</th><th style="text-align:right">Balance</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.print();</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <h1 className="page-title" style={{margin:0}}>Consumers</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Consumer</button>
      </div>
      <div className="filters">
        <input className="input" style={{width:200}} placeholder="Search..." value={filters.search}
          onChange={e => setFilters(f => ({...f, search: e.target.value}))}
          onKeyDown={e => e.key === 'Enter' && load(1)} />
        <select className="input" style={{width:180}} value={filters.barangay_id} onChange={e => setFilters(f => ({...f, barangay_id: e.target.value}))}>
          <option value="">All Barangays</option>
          {barangays.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className="input" style={{width:150}} value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Disconnected">Disconnected</option>
        </select>
      </div>
      {loading ? <div className="loading"><div className="spinner"></div></div> : (
        <>
          <div className="card table-wrap">
            <table>
              <thead><tr><th>Account</th><th>Name</th><th>Barangay</th><th>Mobile</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {consumers.map(c => (
                  <tr key={c.id} onClick={() => openLedger(c.id)} style={{ cursor: 'pointer' }}>
                    <td>{c.account_code}</td><td>{c.name}</td><td>{c.barangay_name}</td><td>{c.mobile || '-'}</td><td>{c.consumer_type}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td>
                      <Link to={`/consumers/${c.id}`} className="btn btn-primary btn-sm" style={{marginRight:4}} onClick={e => e.stopPropagation()}>View</Link>
                      <button className="btn btn-sm" style={{background:'var(--text-secondary)',color:'white'}} onClick={e => { e.stopPropagation(); openEdit(c.id); }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{color:'var(--text-secondary)', fontSize:13}}>{total} consumers</span>
            {!filters.barangay_id && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {!showAll && (
                  <>
                    <button className="btn btn-sm" style={{background:'var(--bg-input)'}} disabled={page <= 1} onClick={() => goPage(page - 1)}>Prev</button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let p;
                      if (totalPages <= 7) p = i + 1;
                      else if (page <= 4) p = i + 1;
                      else if (page >= totalPages - 3) p = totalPages - 6 + i;
                      else p = page - 3 + i;
                      return (
                        <button key={p} className="btn btn-sm" onClick={() => goPage(p)}
                          style={{ background: p === page ? 'var(--accent)' : 'var(--bg-input)', color: p === page ? 'white' : 'var(--text-primary)', minWidth: 32 }}>
                          {p}
                        </button>
                      );
                    })}
                    <button className="btn btn-sm" style={{background:'var(--bg-input)'}} disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next</button>
                  </>
                )}
                <button className="btn btn-sm" style={{background: showAll ? 'var(--accent)' : 'var(--bg-input)', color: showAll ? 'white' : 'var(--text-primary)'}}
                  onClick={() => setShowAll(a => !a)}>
                  {showAll ? 'Paginate' : 'Show All'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Ledger Modal */}
      {ledgerModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setLedgerModal(null)}>
          <div className="card" style={{ width: '90%', maxWidth: 900, maxHeight: '85vh', margin: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            {ledgerLoading ? (
              <div className="loading" style={{ padding: 40 }}><div className="spinner"></div></div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{ledgerModal.consumer?.name}</h3>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                      {ledgerModal.consumer?.account_code} &bull; {ledgerModal.consumer?.barangay_name} &bull; {ledgerModal.consumer?.consumer_type}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm" style={{ background: 'var(--success, #2e7d32)', color: 'white' }} onClick={openAdj}>+ Add Balance</button>
                    <button className="btn btn-primary btn-sm" onClick={printLedger}>Print</button>
                    <button className="btn btn-sm" style={{ background: 'var(--bg-input)', fontSize: 18, lineHeight: 1, padding: '4px 10px' }} onClick={() => { setLedgerModal(null); setAdjOpen(false); }}>&times;</button>
                  </div>
                </div>
                {adjOpen && (
                  <div style={{ background: 'var(--bg-input)', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <h4 style={{ margin: '0 0 8px' }}>Add Balance Adjustment</h4>
                    {adjError && <div style={{ color: 'var(--danger, #d32f2f)', fontSize: 13, marginBottom: 8 }}>{adjError}</div>}
                    <form onSubmit={handleAdjSubmit}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Billing Period *</label>
                          <input className="input" style={{ width: 110 }} placeholder="2026-02" value={adjForm.billing_period}
                            onChange={e => setAdjForm(f => ({ ...f, billing_period: e.target.value }))} required />
                        </div>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Water Charge</label>
                          <input className="input" type="number" step="0.01" min="0" style={{ width: 100 }} value={adjForm.water_charge}
                            onChange={e => setAdjForm(f => ({ ...f, water_charge: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Past Due</label>
                          <input className="input" type="number" step="0.01" min="0" style={{ width: 100 }} value={adjForm.past_due}
                            onChange={e => setAdjForm(f => ({ ...f, past_due: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Penalty</label>
                          <input className="input" type="number" step="0.01" min="0" style={{ width: 100 }} value={adjForm.penalty}
                            onChange={e => setAdjForm(f => ({ ...f, penalty: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Paid Amount</label>
                          <input className="input" type="number" step="0.01" min="0" style={{ width: 100 }} value={adjForm.payment_amount}
                            onChange={e => setAdjForm(f => ({ ...f, payment_amount: e.target.value }))} />
                        </div>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={adjSaving}>{adjSaving ? 'Saving...' : 'Save'}</button>
                        <button type="button" className="btn btn-sm" style={{ background: 'var(--bg-card)' }} onClick={() => setAdjOpen(false)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                )}
                <div style={{ overflow: 'auto', flex: 1 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-right">Prev</th>
                        <th className="text-right">Current</th>
                        <th className="text-right">Consumption</th>
                        <th className="text-right">Billed</th>
                        <th className="text-right">Paid</th>
                        <th className="text-right">Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerModal.ledger?.length === 0 ? (
                        <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No readings found</td></tr>
                      ) : ledgerModal.ledger?.map((l, i) => (
                        <tr key={i}>
                          <td>{fmtDate(l.reading_date)}</td>
                          <td className="text-right">{l.previous_reading ?? '-'}</td>
                          <td className="text-right">{l.current_reading}</td>
                          <td className="text-right">{l.consumption}</td>
                          <td className="text-right">PHP {fmt(l.bill_amount)}</td>
                          <td className="text-right">PHP {fmt(l.paid_amount)}</td>
                          <td className="text-right" style={{ fontWeight: 600, color: l.balance > 0 ? 'var(--danger)' : 'var(--success)' }}>PHP {fmt(l.balance)}</td>
                          <td>{l.status ? billStatusBadge(l.status) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Consumer Modal */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditModal(null)}>
          <div className="card" style={{ width: '90%', maxWidth: 500, maxHeight: '90vh', margin: 16, overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            {editLoading ? (
              <div className="loading" style={{ padding: 40 }}><div className="spinner"></div></div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0 }}>Edit Consumer</h3>
                  <button className="btn btn-sm" style={{ background: 'var(--bg-input)', fontSize: 18, lineHeight: 1, padding: '4px 10px' }} onClick={() => setEditModal(null)}>&times;</button>
                </div>
                {editError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{editError}</div>}
                <form onSubmit={handleEditSubmit}>
                  <div className="form-group">
                    <label className="label">Barangay *</label>
                    <select className="input" value={editForm.barangay_id} onChange={e => setEdit('barangay_id', e.target.value)} required>
                      <option value="">Select...</option>
                      {barangays.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="label">Account Code</label>
                    <input className="input" value={editForm.account_code} readOnly style={{ opacity: 0.6 }} />
                  </div>
                  <div className="form-group">
                    <label className="label">Full Name *</label>
                    <input className="input" value={editForm.name} onChange={e => setEdit('name', e.target.value.toUpperCase())} required />
                  </div>
                  <div className="form-group">
                    <label className="label">Address</label>
                    <input className="input" value={editForm.address} onChange={e => setEdit('address', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="label">Mobile Number</label>
                    <input className="input" value={editForm.mobile} onChange={e => setEdit('mobile', e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="label">Consumer Type</label>
                      <select className="input" value={editForm.consumer_type} onChange={e => setEdit('consumer_type', e.target.value)}>
                        <option>Residential</option><option>Commercial</option><option>Industrial</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="label">Status</label>
                      <select className="input" value={editForm.status} onChange={e => setEdit('status', e.target.value)}>
                        <option>Active</option><option>Inactive</option><option>Disconnected</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button type="button" className="btn" style={{ background: 'var(--bg-input)', color: 'var(--text-primary)' }} onClick={() => setEditModal(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={editSaving}>{editSaving ? 'Saving...' : 'Update'}</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Consumer Modal */}
      {addModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setAddModal(false)}>
          <div className="card" style={{ width: '90%', maxWidth: 500, maxHeight: '90vh', margin: 16, overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Add Consumer</h3>
              <button className="btn btn-sm" style={{ background: 'var(--bg-input)', fontSize: 18, lineHeight: 1, padding: '4px 10px' }} onClick={() => setAddModal(false)}>&times;</button>
            </div>
            {addError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{addError}</div>}
            <form onSubmit={handleAddSubmit}>
              <div className="form-group">
                <label className="label">Barangay *</label>
                <select className="input" value={addForm.barangay_id} onChange={e => { setAdd('barangay_id', e.target.value); fetchNextCode(e.target.value); }} required>
                  <option value="">Select...</option>
                  {barangays.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="label">Account Code *</label>
                <input className="input" value={addForm.account_code} onChange={e => setAdd('account_code', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="label">Full Name *</label>
                <input className="input" value={addForm.name} onChange={e => setAdd('name', e.target.value.toUpperCase())} required />
              </div>
              <div className="form-group">
                <label className="label">Address</label>
                <input className="input" value={addForm.address} onChange={e => setAdd('address', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="label">Mobile Number</label>
                <input className="input" value={addForm.mobile} onChange={e => setAdd('mobile', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="label">Consumer Type</label>
                <select className="input" value={addForm.consumer_type} onChange={e => setAdd('consumer_type', e.target.value)}>
                  <option>Residential</option><option>Commercial</option><option>Industrial</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn" style={{ background: 'var(--bg-input)', color: 'var(--text-primary)' }} onClick={() => setAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={addSaving}>{addSaving ? 'Saving...' : 'Add Consumer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
